import { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const BrandContext = createContext(null)

export const themes = [
  { name: 'GCW Classic',  accent: '#B8734A', sidebar: '#2B2B2B', bg: '#F7F2EB' },
  { name: 'Clean Light',  accent: '#2A7AB5', sidebar: '#1E3A5F', bg: '#FFFFFF' },
  { name: 'Ocean',        accent: '#2A9D8F', sidebar: '#1A3C40', bg: '#F0F8F8' },
  { name: 'Forest',       accent: '#4A7C59', sidebar: '#1E3028', bg: '#F2F7F4' },
  { name: 'Slate',        accent: '#5A6EA0', sidebar: '#1E2340', bg: '#F4F5FA' },
  { name: 'Midnight',     accent: '#7B5EA7', sidebar: '#1A1A2E', bg: '#F5F3FA' },
]

export const defaultBrand = {
  companyName:        'Demo Company Co.',
  monogram:           'DC',
  primaryColor:       '#B8734A',
  sidebarColor:       '#2B2B2B',
  bgColor:            '#F7F2EB',
  textColor:          '#2B2B2B',
  navTextColor:       '#FFFFFF',
  tagline:            'Simplifying Work. Amplifying Results.',
  logoUrl:            null,
  logoSize:           'medium',
  campusLabel:        'Collective Campus',
  timeOffCategories:  ['Vacation','Sick','Personal','Unpaid'],
  departments:        ['Operations','Administrative','Field Staff','Customer Service'],
  features: {
    onboarding:     true,
    policies:       true,
    timesheets:     true,
    timeOff:        true,
    memos:          true,
    incidents:      true,
    performance:    true,
    roomScheduler:  false,
    demoMode:       false,
  },
}

const CLIENT_CONFIG_ID = '35bc45f8-09e8-4bba-9de3-a6c3fd889a08'

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(defaultBrand)
  const [configLoading, setConfigLoading] = useState(true)

  useEffect(() => {
    async function loadConfig() {
      try {
        const { data, error } = await supabase
          .from('client_config')
          .select('*')
          .eq('id', CLIENT_CONFIG_ID)
          .single()

        if (error || !data) { setConfigLoading(false); return }

        setBrand({
          companyName:       data.company_name        || defaultBrand.companyName,
          monogram:          data.monogram            || defaultBrand.monogram,
          primaryColor:      data.primary_color       || defaultBrand.primaryColor,
          sidebarColor:      data.sidebar_color       || defaultBrand.sidebarColor,
          bgColor:           data.bg_color            || defaultBrand.bgColor,
          textColor:         data.text_color          || defaultBrand.textColor,
          tagline:           data.tagline             || defaultBrand.tagline,
          logoUrl:           data.logo_url            || null,
          logoSize:          data.logo_size           || 'medium',
          campusLabel:       data.campus_label        || defaultBrand.campusLabel,
          timeOffCategories: data.time_off_categories || defaultBrand.timeOffCategories,
          departments:       data.departments         || defaultBrand.departments,
          features: {
            ...defaultBrand.features,
            ...(data.features || {}),
          },
        })
      } catch (e) {
        console.warn('Could not load brand config:', e)
      } finally {
        setConfigLoading(false)
      }
    }
    loadConfig()
  }, [])

  async function saveBrand(updates) {
    const next = { ...brand, ...updates }
    setBrand(next)

    try {
      const { error } = await supabase
        .from('client_config')
        .update({
          company_name:        next.companyName,
          monogram:            next.monogram,
          primary_color:       next.primaryColor,
          sidebar_color:       next.sidebarColor,
          bg_color:            next.bgColor,
          text_color:          next.textColor,
          nav_text_color:      next.navTextColor,
          tagline:             next.tagline,
          logo_url:            next.logoUrl,
          logo_size:           next.logoSize,
          campus_label:        next.campusLabel,
          time_off_categories: next.timeOffCategories,
          departments:         next.departments,
          features:            next.features,
          updated_at:          new Date().toISOString(),
        })
        .eq('id', CLIENT_CONFIG_ID)

      if (error) console.warn('Save error:', error)
    } catch (e) {
      console.warn('Could not save brand config:', e)
    }
  }

  return (
    <BrandContext.Provider value={{ brand, setBrand, saveBrand, configLoading }}>
      {children}
    </BrandContext.Provider>
  )
}

export const useBrand = () => useContext(BrandContext)